import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector, useStore } from 'react-redux'
import router from '@Site/Router'
import { IApplicationState } from '@Site/rootReducer'
import difference from 'lodash/difference'
import find from 'lodash/find'
import filter from 'lodash/filter'
import map from 'lodash/map'
import some from 'lodash/some'
import reduce from 'lodash/reduce'
import each from 'lodash/each'
import classnames from 'classnames'
import formattedCreditsWithHours from '@Utils/FormattedCreditsWithHours'
import getSetupCosts from '@Utils/GetSetupCosts'
import Button from '@SiteComponents/Button'
import StepIndicator from '@SiteComponents/StepIndicator'
import Popup from '@SiteComponents/Popup'
import VoiceTypeUpgradePopup from '@SiteContainers/VoiceTypeUpgradePopup'
import Loader from '@SiteComponents/Loader'
import ProjectVersionLanguage from './ProjectVersionLanguage'
import TranslationLanguages from './TranslationLanguages'
import RepurposeInstructions from './RepurposeInstructions'
import Parameters from './Parameters'
import {
  ENHANCE_CLEAN_ORIGINAL_AUDIO,
} from './EnhanceAudioParameter'
import SetupWarningPopup, { type IWarning } from '@SiteComponents/SetupWarningPopup/SetupWarningPopup'
import SetupStylingPopup from './SetupStylingPopup'
import UploadAndImport from './UploadAndImport'
import Cancel from '@Images/new_project_popup_cancel.svg'
import Continue from '@Images/new_project_popup_continue.svg'

import {
  ISubmitProjectVersionUpdatePayload,
} from '@SiteModules/ProjectVersionSettings/types'

import {
  ICreateClipsPayload,
  ITranslationSettings,
} from '@SiteModules/Project/types'

import {
  fetchProjectVersionSettings,
  resetProjectVersionSettings,
  submitProjectVersionUpdate,
  beginProjectSubmission,
  releaseProjectCleanupOwnership,
  fetchTranslationLanguages,
  resetTranslationLanguages,
} from '@SiteModules/ProjectVersionSettings/actions'

import {
  fetchVocabularies,
} from '@SiteModules/Vocabulary/actions'

import {
  fetchTermbases,
} from '@SiteModules/Termbase/actions'

import {
  fetchGuidelines,
} from '@SiteModules/Guideline/actions'

import {
  fetchThemes,
} from '@SiteModules/Theme/actions'

import {
  deleteElement,
} from '@SiteModules/Element/actions'

import {
  closeSetupProjectPopup,
  closeVoiceTypeUpgradePopup,
  openVoiceTypeUpgradePopup,
} from '@SiteModules/Popup/actions'

import {
  deleteProjects,
  createClips,
} from '@SiteModules/Project/actions'

import {
  deleteProjectsOnPageHide,
} from '@SiteModules/Project/api'

import {
  getHasLoadedUserDataSuccessfully
} from '@SiteModules/User/selectors'

import {
  getCurrentWorkspaceId,
} from '@SiteModules/Workspace/selectors'

import {
  getCurrentAccountSubscriptionHasCreditLikeCharging,
  getCurrentAccountHasCurrentSubscription,
  getCurrentAccountHasEnterpriseOrCustomSubscription,
  getCurrentAccountAvailableTotalCredits,
  getCurrentAccountAvailableAnalysisCredits,
  getCurrentAccountSubscriptionAllowsDubbing,
  getCurrentAccountSubscriptionAllowsVoiceCloning,
  getCurrentAccountSubscriptionAllowsTranslationAdaptionForDubbing,
  getCurrentAccountSubscriptionAllowsLipSyncing,
  getCurrentAccountSubscriptionMaxLanguagesLimit,
  getUserIsAccountAdmin,
  getCurrentAccountAdminEmails,
  getCurrentAccountMaxFileSize,
  getCurrentAccountTranslationProvider,
} from '@SiteModules/Account/selectors'

import {
  getHasLoadedProjectVersionSettingsSuccessfully,
  getHasVoice,
  getExistingProjectVersionLanguage,
  getExistingTranslationLanguages,
  getProjectVersionDuration,
  getSubscriptionSubtitlesLength,
  getDubbingLanguages,
  getVoiceCloningLanguages,
  getAlignmentLanguages,
  getProjectTermbaseName,
  getLastUsedReviewTranscript,
  getLastUsedVocabularyName,
  getLastUsedSubtitleLanguages,
  getLastUsedTranscriptLanguage,
  getLastUsedSeparateAudio,
  getLastUsedAdaptTranslation,
  getLastUsedCaptionGuidelineName,
  getLastUsedTranslationGuidelineName,
  getLastTranslationCreatedAfterGuidelineDeployment,
  getLastUsedTermbaseName,
  getLastUsedThemeName,
  getLastUsedSpeakerDiarization,
  getLastUsedFormality,
  getLastUsedLipSyncModel,
  getLastUsedReviewForDubbing,
  getLastUsedReviewForRepurpose,
  getElementsForProjectVersions,
  getProjectIdsPendingCleanup,
  getProjectSubmissionIsPending,
  getHasNoLastCreatedSubtitle,
  getSubmittingUrls,
  getHasDownloadingElementsForProjectVersions,
  getAvailableTranslationLanguages,
  getAvailableProjectVersionLanguages,
  getNonTranslatableLanguages,
  getFetchingTranslationLanguages,
} from '@SiteModules/ProjectVersionSettings/selectors'

import {
  getEditingProjectVersionIds,
  getProjectType,
  getNewProjectPopupIsOpen,
  getVoiceTypeUpgradePopupLabel,
  getProjectIdOfFirstEditingProjectVersion,
} from '@SiteModules/Popup/selectors'

import {
  getVocabulariesHasLoadedInitially,
  getVocabulariesLoading,
  getVocabularies,
} from '@SiteModules/Vocabulary/selectors'

import {
  getGuidelinesHasLoadedInitially,
  getGuidelinesLoading,
  getTranslationGuidelines,
  getCaptionGuidelines,
} from '@SiteModules/Guideline/selectors'

import {
  getTermbasesHasLoadedInitially,
  getTermbasesLoading,
  getTermbases,
} from '@SiteModules/Termbase/selectors'

import {
  getThemesHasLoadedInitially,
  getThemes,
} from '@SiteModules/Theme/selectors'

import {
  getVoiceCloningEngineId,
  getVoiceCloningEngineOptionsForLanguages,
  getResolvedVoiceCloningModelIdForLanguages,
  isOdubVoiceCloningModelId,
  STANDARD_TTS_MODEL_LABEL,
} from '@Utils/OdubTtsModels'
import './SetupProjectPopup.scss'

const DEFAULT_GUIDELINE_NAME = 'AI ENHANCEMENT'
const DEFAULT_THEME_ID = -1 // Default theme
const DELAY_WAITING_FOR_HANDLE_CLOSE = 500 // ms
const VOICE_TYPE_UPGRADE_POPUP_Z_INDEX = 2000000 // Higher than any other popup, so that it is always on top
const FACTOR_FOR_CLIP_CHARGING = 1 // multiplied by the duration of the original project_version
const ESTIMATED_CLIP_LENGTH = 0.2 // of the duration of the original project_version
const REVIEW_SOURCE_TRANSCRIPT_OPTION = 'review_source_transcript'
const REVIEW_CLIP_TRANSCRIPT_OPTION = 'review_clip_transcript'
const REVIEW_TRANSLATIONS_OPTION = 'review_translations'

const SetupProjectPopup: React.FC = () => {
  const dispatch = useDispatch()
  const store = useStore<IApplicationState>()

  const currentWorkspaceId = useSelector(getCurrentWorkspaceId)
  const newProjectPopupIsOpen = useSelector(getNewProjectPopupIsOpen)
  const voiceTypeUpgradePopupLabel = useSelector(getVoiceTypeUpgradePopupLabel)

  const hasLoadedUserDataSuccessFully = useSelector(getHasLoadedUserDataSuccessfully)
  const hasLoadedProjectVersionSettingsSuccessfully = useSelector(getHasLoadedProjectVersionSettingsSuccessfully)
  const vocabulariesHasLoadedInitially = useSelector(getVocabulariesHasLoadedInitially)
  const vocabulariesLoading = useSelector(getVocabulariesLoading)
  const guidelinesHasLoadedInitially = useSelector(getGuidelinesHasLoadedInitially)
  const guidelinesLoading = useSelector(getGuidelinesLoading)
  const termbasesHasLoadedInitially = useSelector(getTermbasesHasLoadedInitially)
  const termbasesLoading = useSelector(getTermbasesLoading)
  const themesHasLoadedInitially = useSelector(getThemesHasLoadedInitially)
  const loading = vocabulariesLoading || guidelinesLoading || termbasesLoading
  const hasLoadedSuccessfully = !loading && hasLoadedUserDataSuccessFully && hasLoadedProjectVersionSettingsSuccessfully && vocabulariesHasLoadedInitially && termbasesHasLoadedInitially && guidelinesHasLoadedInitially && themesHasLoadedInitially

  const editingProjectVersionIds = useSelector(getEditingProjectVersionIds)
  const projectIdOfFirstEditingProjectVersion = useSelector(getProjectIdOfFirstEditingProjectVersion)
  const hasVoice = useSelector(getHasVoice)
  const existingProjectVersionLanguage = useSelector(getExistingProjectVersionLanguage)
  const projectVersionDurationForExistingProjects = useSelector(getProjectVersionDuration)
  const subscriptionSubtitlesLength = useSelector(getSubscriptionSubtitlesLength)
  const dubbingLanguages = useSelector(getDubbingLanguages)
  const voiceCloningLanguages = useSelector(getVoiceCloningLanguages)
  const alignmentLanguages = useSelector(getAlignmentLanguages)
  const currentAccountSubscriptionHasCreditLikeCharging = useSelector(getCurrentAccountSubscriptionHasCreditLikeCharging)
  const currentAccountHasCurrentSubscription = useSelector(getCurrentAccountHasCurrentSubscription)
  const currentAccountSubscriptionAllowsDubbing = useSelector(getCurrentAccountSubscriptionAllowsDubbing)
  const currentAccountSubscriptionAllowsVoiceCloning = useSelector(getCurrentAccountSubscriptionAllowsVoiceCloning)
  const currentAccountSubscriptionAllowsTranslationAdaptionForDubbing = useSelector(getCurrentAccountSubscriptionAllowsTranslationAdaptionForDubbing)
  const currentAccountSubscriptionAllowsLipSyncing = useSelector(getCurrentAccountSubscriptionAllowsLipSyncing)
  const currentAccountSubscriptionMaxLanguagesLimit = useSelector(getCurrentAccountSubscriptionMaxLanguagesLimit) || 0
  const currentAccountAvailableTotalCredits = useSelector(getCurrentAccountAvailableTotalCredits)
  const currentAccountAvailableAnalysisCredits = useSelector(getCurrentAccountAvailableAnalysisCredits)
  const userIsAccountAdmin = useSelector(getUserIsAccountAdmin)
  const currentAccountAdminEmails = useSelector(getCurrentAccountAdminEmails)
  const currentAccountHasEnterpriseOrCustomSubscription = useSelector(getCurrentAccountHasEnterpriseOrCustomSubscription)

  const vocabularies = useSelector(getVocabularies)
  const captionGuidelines = useSelector(getCaptionGuidelines)
  const translationGuidelines = useSelector(getTranslationGuidelines)
  const termbases = useSelector(getTermbases)
  const themes = useSelector(getThemes)
  const projectTermbaseName = useSelector(getProjectTermbaseName)

  const lastUsedTranscriptLanguage = useSelector(getLastUsedTranscriptLanguage)
  const lastUsedSubtitleLanguages = useSelector(getLastUsedSubtitleLanguages)
  const lastUsedReviewTranscript = useSelector(getLastUsedReviewTranscript)
  const lastUsedVocabularyName = useSelector(getLastUsedVocabularyName)
  const lastUsedSeparateAudio = useSelector(getLastUsedSeparateAudio)
  const lastUsedAdaptTranslation = useSelector(getLastUsedAdaptTranslation)
  const lastUsedCaptionGuidelineName = useSelector(getLastUsedCaptionGuidelineName)
  const lastUsedTermbaseName = useSelector(getLastUsedTermbaseName)
  const lastUsedTranslationGuidelineName = useSelector(getLastUsedTranslationGuidelineName)
  const lastTranslationCreatedAfterGuidelineDeployment = useSelector(getLastTranslationCreatedAfterGuidelineDeployment)
  const lastUsedThemeName = useSelector(getLastUsedThemeName)
  const lastUsedSpeakerDiarization = useSelector(getLastUsedSpeakerDiarization)
  const lastUsedFormality = useSelector(getLastUsedFormality)
  const lastUsedLipSyncModel = useSelector(getLastUsedLipSyncModel)
  const lastUsedReviewForDubbing = useSelector(getLastUsedReviewForDubbing)
  const lastUsedReviewForRepurpose = useSelector(getLastUsedReviewForRepurpose)
  const hasNoLastCreatedSubtitle = useSelector(getHasNoLastCreatedSubtitle)
  const elementsForProjectVersions = useSelector(getElementsForProjectVersions)
  const projectIdsPendingCleanup = useSelector(getProjectIdsPendingCleanup)
  const transferProjectCleanupOwnership = () => {
    dispatch(releaseProjectCleanupOwnership())
  }
  const submittingUrls = useSelector(getSubmittingUrls)
  const hasDownloadingElementsForProjectVersions = useSelector(getHasDownloadingElementsForProjectVersions)
  const maxFileSize = useSelector(getCurrentAccountMaxFileSize)
  const availableProjectVersionLanguages = useSelector(getAvailableProjectVersionLanguages)
  const availableTranslationLanguages = useSelector(getAvailableTranslationLanguages)
  const nonTranslatableLanguages = useSelector(getNonTranslatableLanguages)
  const translationProvider = useSelector(getCurrentAccountTranslationProvider)
  const fetchingTranslationLanguages = useSelector(getFetchingTranslationLanguages)

  const isSubmittingUrls = submittingUrls.length > 0
  let projectType = useSelector(getProjectType)

  const shouldRepurpose = projectType === 'repurpose'
  const existingTranslationLanguages = shouldRepurpose ? [] : useSelector(getExistingTranslationLanguages)
  const existingProjectWithTranscriptOnlyAndNoVoice = existingProjectVersionLanguage.length > 0 && existingTranslationLanguages.length === 0 && !hasVoice
  if (!projectType) {
    projectType =  hasVoice || existingProjectWithTranscriptOnlyAndNoVoice ? 'dubbing' : 'subtitles'
  }

  const isDubbingProject = projectType === 'dubbing'
  const isSubtitlesProject = projectType === 'subtitles' || projectType === 'repurpose'

  const [newProjectVersionLanguage, setNewProjectVersionLanguage] = useState('')
  const [newTranslationSettings, setNewTranslationSettings] = useState<ITranslationSettings[]>([])
  const [shouldTranslate, setShouldTranslate] = useState(false)
  const [showMissingFileAlert, setShowMissingFileAlert] = useState(false)
  const [showFilesTooLargeAlert, setShowFilesTooLargeAlert] = useState(false)
  const [showProjectVersionLanguageAlert, setShowProjectVersionLanguageAlert] = useState(false)
  const [showTranslationLanguagesAlert, setShowTranslationLanguagesAlert] = useState(false)
  const [showTotalCreditsAlert, setShowTotalCreditsAlert] = useState(false)
  const [showAnalysisCreditsAlert, setShowAnalysisCreditsAlert] = useState(false)
  const [reviewModeOption, setReviewModeOption] = useState<string | null>('Review transcript (best quality)')
  const [reviewModeOptionsForRepurpose, setReviewModeOptionsForRepurpose] = useState<string[]>([])
  const [reviewModeOptionsForDubbing, setReviewModeOptionsForDubbing] = useState<string[]>([])
  const [styleOption, setStyleOption] = useState<string | null>(null)
  const [vocabularyOption, setVocabularyOption] = useState<string | null>(null)
  const [termbaseOption, setTermbaseOption] = useState<string | null>(null)
  const [captionGuidelineOption, setCaptionGuidelineOption] = useState<string | null>(null)
  const [defaultTranslationGuidelineOption, setDefaultTranslationGuidelineOption] = useState<string | null>(null)
  const [speakerDetectionOption, setSpeakerDetectionOption] = useState<string | null>('Multiple speakers')
  const [translationToneOption, setTranslationToneOption] = useState<string | null>('Auto tone')
  const [dubbingModeOption, setDubbingModeOption] = useState<string | null>(currentAccountSubscriptionAllowsVoiceCloning ? 'Voice-cloning' : null)
  const [odubModelOption, setOdubModelOption] = useState<string | null>(STANDARD_TTS_MODEL_LABEL)
  const [rephrasingForDubbingOption, setRephrasingForDubbingOption] = useState<string | null>('Rephrase for dubbing')
  const [isolateVoicesOption, setIsolateVoicesOption] = useState<string | null>('Isolate voices')
  const [lipSyncOption, setLipSyncOption] = useState<string | null>(null)
  const [enhanceAudioOptions, setEnhanceAudioOptions] = useState<string[]>([])
  const [outputFormatOption, setOutputFormatOption] = useState('format_9_16')
  const [transcriptText, setTranscriptText] = useState('')
  const [hasSrt, setHasSrt] = useState(false)
  const projectSubmissionIsPending = useSelector(getProjectSubmissionIsPending)
  const [warningsToShow, setWarningsToShow] = useState<IWarning[]>([])
  const [ignoredWarnings, setIgnoredWarnings]= useState<string[]>([])
  const [hasStartedFirstUploadOrSubmit, setHasStartedFirstUploadOrSubmit] = useState(false)
  const [showStylingPopup, setShowStylingPopup] = useState(false)
  const [transcriptFileName, setTranscriptFileName] = useState<string | null>(null)
  const [repurposeInstructions, setRepurposeInstructions] = useState<string | undefined>(undefined)

  const isNonExistingProject = editingProjectVersionIds.length === 0
  const isForMultipleProjects = isNonExistingProject ? elementsForProjectVersions.length > 1 : editingProjectVersionIds.length > 1
  const hasNewTranscription = existingProjectVersionLanguage.length === 0
  const isNewSubtitlesProject = isSubtitlesProject && hasNewTranscription
  const isNewSubtitlesProjectWithoutTranslation =  isNewSubtitlesProject && !shouldTranslate
  const isNewSubtitlesProjectWithTranslation = isNewSubtitlesProject && shouldTranslate
  const isAddingLanguagesToExistingSubtitlesProject = isSubtitlesProject && !hasNewTranscription && !shouldRepurpose
  const isNewDubbingProject = isDubbingProject && hasNewTranscription && !shouldRepurpose
  const isAddingLanguagesToExistingDubbingProject = isDubbingProject && !hasNewTranscription
  const isAddingLanguagesToExistingProject = isAddingLanguagesToExistingSubtitlesProject || isAddingLanguagesToExistingDubbingProject
  const isNewProject = isNewSubtitlesProject || isNewDubbingProject
  const isAddingClipsToExistingProject = !hasNewTranscription && shouldRepurpose
  const isAddingClipsToExistingProjectWithoutTranslation = isAddingClipsToExistingProject && !shouldTranslate
  const isAddingClipsToExistingProjectWithTranslation = isAddingClipsToExistingProject && shouldTranslate

  const newTranslationLanguages = map(newTranslationSettings, currentNewTranslationSettings => currentNewTranslationSettings.language)
  const dubbingTargetLanguages = newTranslationLanguages
  const newSubtitlesLength = hasNewTranscription || isAddingClipsToExistingProject ? newTranslationLanguages.length + 1 : newTranslationLanguages.length
  const totalNumberOfSubscriptionSubtitles = isAddingClipsToExistingProject ? newSubtitlesLength : subscriptionSubtitlesLength + newSubtitlesLength
  const isLanguageLimitReached = currentAccountSubscriptionMaxLanguagesLimit < totalNumberOfSubscriptionSubtitles

  const newTranslationLanguagesThatDoNotAllowDubbing = difference(newTranslationLanguages, dubbingLanguages)
  const newTranslationLanguagesThatDoNotAllowVoiceCloning = difference(newTranslationLanguages, voiceCloningLanguages)
  const projectVersionLanguage = hasNewTranscription ? newProjectVersionLanguage : existingProjectVersionLanguage
  const projectVersionLanguageAllowsVoiceCloning = voiceCloningLanguages.includes(projectVersionLanguage)
  const newProjectVersionLanguageAllowsTextTranscriptImport = newProjectVersionLanguage.length > 0 && alignmentLanguages.includes(newProjectVersionLanguage)
  const projectVersionLanguageIsTranslatable = isNewProject ? (newProjectVersionLanguage.length === 0 || !nonTranslatableLanguages.includes(newProjectVersionLanguage)) : !nonTranslatableLanguages.includes(existingProjectVersionLanguage)

  const willTranslate = isNewSubtitlesProjectWithTranslation || isAddingLanguagesToExistingSubtitlesProject || isDubbingProject || isAddingClipsToExistingProjectWithTranslation
  const cannotImportScript = isForMultipleProjects
  const shouldDub = isDubbingProject && !!dubbingModeOption
  const willImportTranscript = !isForMultipleProjects && transcriptText.length > 0 && (hasSrt || newProjectVersionLanguageAllowsTextTranscriptImport)
  const willImportAnyTranslationSrt = !isForMultipleProjects && some(newTranslationSettings, currentNewTranslationSettings => !!currentNewTranslationSettings.srtTranslationText && currentNewTranslationSettings.srtTranslationText.length > 0 )
  const willReviewTranscriptForRepurposingProject = reviewModeOptionsForRepurpose.includes(REVIEW_CLIP_TRANSCRIPT_OPTION)
  const willReviewTranscriptForDubbingProject = reviewModeOptionsForDubbing.includes(REVIEW_SOURCE_TRANSCRIPT_OPTION)
  const willReviewTranscriptForOtherProject = hasNewTranscription && reviewModeOption === 'Review transcript (best quality)'

  let willReviewTranscript = willReviewTranscriptForOtherProject
  if (shouldRepurpose) {
    willReviewTranscript = willReviewTranscriptForRepurposingProject
  } else if (shouldDub) {
    willReviewTranscript = willReviewTranscriptForDubbingProject
  }

  const willReviewOriginal = shouldRepurpose && reviewModeOptionsForRepurpose.includes(REVIEW_SOURCE_TRANSCRIPT_OPTION)
  const willReviewTranslations = shouldDub && reviewModeOptionsForDubbing.includes(REVIEW_TRANSLATIONS_OPTION)

  const willUseVocabulary = hasNewTranscription && !willImportTranscript && !!vocabularyOption
  const willUseTermbase = !!termbaseOption
  const willUseCaptionGuideline = hasNewTranscription && !!captionGuidelineOption
  const willDetectSpeakers = hasNewTranscription && speakerDetectionOption === 'Multiple speakers'
  const willUseTranslationTone = willTranslate && translationToneOption !== 'Auto tone'// In the backend, the translation tone is only applied if the language has a variable translation tone
  const willDub = shouldDub &&
    newTranslationLanguagesThatDoNotAllowDubbing.length === 0 &&
    newTranslationLanguagesThatDoNotAllowVoiceCloning.length === 0 &&
    projectVersionLanguageAllowsVoiceCloning &&
    currentAccountSubscriptionAllowsDubbing &&
    currentAccountSubscriptionAllowsVoiceCloning
  const shouldRephraseForDubbing = shouldDub && rephrasingForDubbingOption === 'Rephrase for dubbing'
  const willRephraseForDubbing = willDub && rephrasingForDubbingOption === 'Rephrase for dubbing' && currentAccountSubscriptionAllowsTranslationAdaptionForDubbing
  const willIsolateVoices = willDub && isolateVoicesOption === 'Isolate voices'
  const shouldSyncLips = shouldDub && !!lipSyncOption
  const willSyncLips = willDub && !!lipSyncOption && currentAccountSubscriptionAllowsLipSyncing

  const fileSelectionIncomplete = isNonExistingProject && hasStartedFirstUploadOrSubmit && elementsForProjectVersions.length === 0
  const filesTooLarge = some(elementsForProjectVersions, elementForProjectVersion => elementForProjectVersion.size > maxFileSize)
  const projectVersionLanguageSelectionIncomplete = (isNewSubtitlesProject || isNewDubbingProject) && newProjectVersionLanguage.length === 0
  const translationLanguageSelectionIncomplete= (isNewSubtitlesProjectWithTranslation || isAddingLanguagesToExistingSubtitlesProject || isDubbingProject || isAddingClipsToExistingProjectWithTranslation) && newTranslationLanguages.length === 0
  const languageSelectionIncomplete = projectVersionLanguageSelectionIncomplete || translationLanguageSelectionIncomplete

  const showWarning = warningsToShow.length > 0
  const showUpload = isNonExistingProject
  const showMain = !isNonExistingProject || hasStartedFirstUploadOrSubmit
  const firstUploadNeeded = isNonExistingProject && !hasStartedFirstUploadOrSubmit
  const uploadingInProgress = some(elementsForProjectVersions, element => !element.projectVersionId)
  const projectVersionIds = isNonExistingProject ? map(elementsForProjectVersions, element => element.projectVersionId || '') : editingProjectVersionIds
  const projectVersionDuration = elementsForProjectVersions.length > 0 ? reduce(elementsForProjectVersions, (sum, element) => sum + (element.duration ?? 0), 0) : projectVersionDurationForExistingProjects

  const isBusy = uploadingInProgress || isSubmittingUrls || hasDownloadingElementsForProjectVersions
  const shouldDisableCTAButton = showMissingFileAlert ||
    showFilesTooLargeAlert ||
    showProjectVersionLanguageAlert ||
    showTranslationLanguagesAlert ||
    !hasLoadedSuccessfully ||
    firstUploadNeeded ||
    isBusy ||
    projectSubmissionIsPending

  const shouldShowUploadingButtonInsteadOfContinue = isBusy
  const hasTranscriptSrtWithoutGuideline = willImportTranscript && hasSrt && !willUseCaptionGuideline
  const numberOfNewTranslationSubtitlesWithSrtWithoutGuideline = filter(newTranslationSettings, currentNewTranslationSettings => {
    const {
      translationGuidelineOption,
      srtTranslationText,
    } = currentNewTranslationSettings

    const willImportTranslationSrt = !isForMultipleProjects && !!srtTranslationText && srtTranslationText.length > 0
    const willUseTranslationGuideline = !!translationGuidelineOption
    return willImportTranslationSrt && !willUseTranslationGuideline
  }).length

  const totalAnalysisCost = shouldRepurpose ? FACTOR_FOR_CLIP_CHARGING * projectVersionDuration : 0
  const estimatedClipTranslationCosts = shouldRepurpose && willTranslate && currentAccountSubscriptionHasCreditLikeCharging ? ESTIMATED_CLIP_LENGTH * (newTranslationLanguages.length - numberOfNewTranslationSubtitlesWithSrtWithoutGuideline) * projectVersionDuration : 0
  const totalCosts = shouldRepurpose ? estimatedClipTranslationCosts : getSetupCosts(
    projectVersionDuration,
    hasNewTranscription,
    hasTranscriptSrtWithoutGuideline,
    newTranslationLanguages.length,
    subscriptionSubtitlesLength,
    willDub,
    willSyncLips,
    lipSyncOption,
    currentAccountSubscriptionHasCreditLikeCharging,
    numberOfNewTranslationSubtitlesWithSrtWithoutGuideline
  )

  const hasEnoughTotalCredits = currentAccountAvailableTotalCredits >= totalCosts
  const hasEnoughAnalysisCredits = currentAccountAvailableAnalysisCredits >= totalAnalysisCost
  const willGenerateShortenedProjectVersion = hasNewTranscription && !willImportTranscript && !willImportAnyTranslationSrt && !hasEnoughTotalCredits && !shouldRepurpose
  const shouldHideCosts = willGenerateShortenedProjectVersion || !projectVersionDuration || uploadingInProgress
  const shouldHideTotalCosts = shouldHideCosts || shouldRepurpose
  const shouldHideAnalysisCosts = shouldHideCosts || !shouldRepurpose
  const shouldHideEstimatedClipTranslationCosts = shouldHideCosts || !shouldRepurpose || estimatedClipTranslationCosts === 0

  let header = 'Add new languages to the project'
  if (isNewDubbingProject) {
    header = 'Generate dubbing'
  } else if (isNewSubtitlesProject && !shouldRepurpose) {
    header = 'Generate subtitles'
  } else if (isNewSubtitlesProject && shouldRepurpose) {
    header = 'Generate a repurposing project'
  } else if (shouldRepurpose) {
    header = 'Generate clips'
  }

  useEffect(() => {
    const fetchPayload = { workspaceId: currentWorkspaceId }

    dispatch(fetchProjectVersionSettings(
      {
        ...fetchPayload,
        ...(isNonExistingProject ? {} : { projectVersionIds: editingProjectVersionIds }),
      }
    ))

    if (!vocabulariesHasLoadedInitially && !vocabulariesLoading) {
      dispatch(fetchVocabularies(fetchPayload))
    }

    if (!guidelinesHasLoadedInitially && !guidelinesLoading) {
      dispatch(fetchGuidelines(fetchPayload))
    }

    if (!termbasesHasLoadedInitially && !termbasesLoading) {
      dispatch(fetchTermbases(fetchPayload))
    }

    dispatch(fetchThemes())

    return () => {
      dispatch(closeSetupProjectPopup())
      dispatch(resetProjectVersionSettings())
    }
  }, [])

  useEffect(() => {
    const handlePageHide = () => {
      const projectVersionSettings = store.getState().projectVersionSettings
      if (projectVersionSettings.projectCleanupOwnership !== 'setup') { return }

      const projectIds = projectVersionSettings.projectIdsPendingCleanup
      if (projectIds.length === 0) { return }

      void deleteProjectsOnPageHide({ projectIds }).catch(() => undefined)
    }

    window.addEventListener('pagehide', handlePageHide)

    return () => {
      window.removeEventListener('pagehide', handlePageHide)
    }
  }, [])

  useEffect(() => {
    if (hasLoadedSuccessfully) {
      let initialProjectVersionLanguage = existingProjectVersionLanguage

      if (hasNewTranscription && lastUsedTranscriptLanguage) {
        initialProjectVersionLanguage = lastUsedTranscriptLanguage
        setNewProjectVersionLanguage(lastUsedTranscriptLanguage)
      }

      if (hasNewTranscription && existingTranslationLanguages.length === 0 && lastUsedSubtitleLanguages) {
        const initialTranslationLanguages = filter(lastUsedSubtitleLanguages, subtitleLanguage => subtitleLanguage !== initialProjectVersionLanguage)
        setNewTranslationSettings(map(initialTranslationLanguages, initialTranslationLanguage => (
          { language: initialTranslationLanguage }
        )))

        if (isNewSubtitlesProject && initialTranslationLanguages.length > 0) {
          setShouldTranslate(true)
        }
      }

      const vocabularyNames = map(vocabularies, vocabulary => vocabulary.attributes.name)
      if (lastUsedVocabularyName && vocabularyNames.includes(lastUsedVocabularyName)) {
        setVocabularyOption(lastUsedVocabularyName)
      }

      const termbaseNames = map(termbases, termbase => termbase.attributes.name)
      if (projectTermbaseName && termbaseNames.includes(projectTermbaseName)) {
        setTermbaseOption(projectTermbaseName)
      } else if (lastUsedTermbaseName && termbaseNames.includes(lastUsedTermbaseName)) {
        setTermbaseOption(lastUsedTermbaseName)
      }

      const themeNames = map(themes, theme => theme.attributes.name)
      if (lastUsedThemeName && themeNames.includes(lastUsedThemeName)) {
        setStyleOption(lastUsedThemeName)
      }

      const captionGuidelineNames = map(captionGuidelines, captionGuideline => captionGuideline.attributes.name)
      const hasDefaultCaptionGuideline = captionGuidelineNames.includes(DEFAULT_GUIDELINE_NAME)
      if (lastUsedCaptionGuidelineName && captionGuidelineNames.includes(lastUsedCaptionGuidelineName)) {
        setCaptionGuidelineOption(lastUsedCaptionGuidelineName)
      } else if (hasDefaultCaptionGuideline) {
        setCaptionGuidelineOption(DEFAULT_GUIDELINE_NAME)
      }

      const translationGuidelineNames = map(translationGuidelines, translationGuideline => translationGuideline.attributes.name)
      if (lastUsedTranslationGuidelineName && translationGuidelineNames.includes(lastUsedTranslationGuidelineName)) {
        setDefaultTranslationGuidelineOption(lastUsedTranslationGuidelineName)
      } else if (hasNoLastCreatedSubtitle || (typeof(lastTranslationCreatedAfterGuidelineDeployment) === 'boolean' && !lastTranslationCreatedAfterGuidelineDeployment) && translationGuidelineNames.includes(DEFAULT_GUIDELINE_NAME)) {
        setDefaultTranslationGuidelineOption(DEFAULT_GUIDELINE_NAME)
      }

      if (typeof(lastUsedSpeakerDiarization) === 'boolean' && !lastUsedSpeakerDiarization) {
        setSpeakerDetectionOption('One speaker')
      }

      if (existingProjectWithTranscriptOnlyAndNoVoice || !currentAccountSubscriptionAllowsVoiceCloning) {
        setDubbingModeOption(null)
      } else if (isDubbingProject) {
        setDubbingModeOption('Voice-cloning')
      }

      if ((typeof(lastUsedReviewTranscript) === 'boolean' && !lastUsedReviewTranscript) || (typeof(lastUsedReviewTranscript) !== 'boolean' && isDubbingProject)) {
        setReviewModeOption('Fast translation')
      }

      if (lastUsedFormality && lastUsedFormality === 'more') {
        setTranslationToneOption('Formal tone')
      } else if (lastUsedFormality && lastUsedFormality === 'less') {
        setTranslationToneOption('Informal tone')
      }

      if (existingProjectWithTranscriptOnlyAndNoVoice || (typeof(lastUsedAdaptTranslation) === 'boolean' && !lastUsedAdaptTranslation)) {
        setRephrasingForDubbingOption(null)
      }

      if (existingProjectWithTranscriptOnlyAndNoVoice || (typeof(lastUsedSeparateAudio) === 'boolean' && !lastUsedSeparateAudio)) {
        setIsolateVoicesOption('No voice isolation')
      }

      if (!existingProjectWithTranscriptOnlyAndNoVoice) {
        if (lastUsedLipSyncModel && lastUsedLipSyncModel === 'Fox') {
          setLipSyncOption('Fox model')
        } else if (lastUsedLipSyncModel && lastUsedLipSyncModel === 'Tiger') {
          setLipSyncOption('Tiger model')
        } else if (lastUsedLipSyncModel && lastUsedLipSyncModel === 'Dragon') {
          setLipSyncOption('Dragon model')
        }
      }

      if (lastUsedReviewForDubbing) {
        let newLastUsedReviewForDubbing = lastUsedReviewForDubbing

        if (!hasNewTranscription) {
          newLastUsedReviewForDubbing = filter(newLastUsedReviewForDubbing, newLastUsedReviewForDubbingOption => newLastUsedReviewForDubbingOption !== REVIEW_SOURCE_TRANSCRIPT_OPTION)
        }
        setReviewModeOptionsForDubbing(newLastUsedReviewForDubbing)
      }

      if (lastUsedReviewForRepurpose) {
        let newLastUsedReviewForRepurpose = lastUsedReviewForRepurpose

        if (!hasNewTranscription) {
          newLastUsedReviewForRepurpose = filter(newLastUsedReviewForRepurpose, newLastUsedReviewForRepurposeOption => newLastUsedReviewForRepurposeOption !== REVIEW_SOURCE_TRANSCRIPT_OPTION)
        }

        if (!willTranslate) {
          newLastUsedReviewForRepurpose = filter(newLastUsedReviewForRepurpose, newLastUsedReviewForRepurposeOption => newLastUsedReviewForRepurposeOption !== REVIEW_CLIP_TRANSCRIPT_OPTION)
        }
        setReviewModeOptionsForRepurpose(newLastUsedReviewForRepurpose)
      }
    }
  }, [hasLoadedSuccessfully])

  useEffect(() => {
    if (!fileSelectionIncomplete && showMissingFileAlert) {
      setShowMissingFileAlert(false)
    }
  }, [fileSelectionIncomplete])

  useEffect(() => {
    if (!filesTooLarge && showFilesTooLargeAlert) {
      setShowFilesTooLargeAlert(false)
    }
  }, [filesTooLarge])

  useEffect(() => {
    if (!projectVersionLanguageSelectionIncomplete && showProjectVersionLanguageAlert) {
      setShowProjectVersionLanguageAlert(false)
    }
  }, [projectVersionLanguageSelectionIncomplete])

  useEffect(() => {
    if (!translationLanguageSelectionIncomplete && showTranslationLanguagesAlert) {
      setShowTranslationLanguagesAlert(false)
    }
  }, [translationLanguageSelectionIncomplete])

  useEffect(() => {
    if (hasEnoughTotalCredits && showTotalCreditsAlert) {
      setShowTotalCreditsAlert(false)
    }
  }, [hasEnoughTotalCredits])

  useEffect(() => {
    if (hasEnoughAnalysisCredits && showAnalysisCreditsAlert) {
      setShowAnalysisCreditsAlert(false)
    }
  }, [hasEnoughAnalysisCredits])

  useEffect(() => {
    if (ignoredWarnings.length > 0) {
      submit()
    }
  }, [ignoredWarnings])

  useEffect(() => {
    const voiceCloningEngineIsAvailable = getVoiceCloningEngineOptionsForLanguages(dubbingTargetLanguages).some(option => option.label === odubModelOption)

    if (odubModelOption && !voiceCloningEngineIsAvailable) {
      setOdubModelOption(STANDARD_TTS_MODEL_LABEL)
    }
  }, [dubbingTargetLanguages, odubModelOption])

  const handleFetchTranslationLanguages = () => {
    if (projectVersionLanguageSelectionIncomplete) {
      dispatch(resetTranslationLanguages())
    } else {
      dispatch(fetchTranslationLanguages({
        workspaceId: currentWorkspaceId,
        projectVersionLanguage,
        ...(isNonExistingProject ? {} : { projectVersionIds: editingProjectVersionIds }),
      }))
    }
  }

  const handleClose = () => {
    transferProjectCleanupOwnership()

    each(elementsForProjectVersions, elementForProjectVersion => {
      const { elementId, projectId } = elementForProjectVersion
      if (projectId) {
        dispatch(deleteElement({ elementId }))
      }
    })

    if (projectIdsPendingCleanup.length > 0) {
      dispatch(deleteProjects({ projectIds: projectIdsPendingCleanup }))
    }
    dispatch(closeSetupProjectPopup())
  }

  const handleCloseAndKeepEverything = () => {
    transferProjectCleanupOwnership()
    dispatch(closeSetupProjectPopup())
  }

  const handleContinue = () => {
    if (isDubbingProject && dubbingModeOption === 'Voice-cloning' && !currentAccountSubscriptionAllowsVoiceCloning) {
      dispatch(openVoiceTypeUpgradePopup({ voiceType: 'Voice Cloning' }))
      return
    } else if (fileSelectionIncomplete || languageSelectionIncomplete || filesTooLarge) {
      if (fileSelectionIncomplete) {
        setShowMissingFileAlert(true)
      }

      if (filesTooLarge) {
        setShowFilesTooLargeAlert(true)
      }

      if (projectVersionLanguageSelectionIncomplete) {
        setShowProjectVersionLanguageAlert(true)
      }

      if (translationLanguageSelectionIncomplete) {
        setShowTranslationLanguagesAlert(true)
      }
    } else {
      submit()
    }
  }

  const resetWarnings = () => {
    setWarningsToShow([])
    setIgnoredWarnings([])
  }

  const updateIgnoredWarningsAndTrySubmit = (warningsToIgnore: string[]) => {
    setIgnoredWarnings(ignoredWarnings.concat(warningsToIgnore))
    // submit is called in useEffect
  }

  const submit = () => {
    const newWarningsToShow = getWarningsToShow()
    setWarningsToShow(newWarningsToShow)

    if (newWarningsToShow.length === 0) {
      let formality: string | null = null

      switch(translationToneOption) {
        case 'Auto tone':
          formality = 'default'
          break
        case 'Formal tone':
          formality = 'more'
          break
        case 'Informal tone':
          formality = 'less'
          break
      }

      const selectedTheme = find(themes, theme => styleOption === theme.attributes.name)
      const themeId = selectedTheme ? selectedTheme.id : null

      const selectedTermbase = find(termbases, termbase => termbaseOption === termbase.attributes.name)
      const termbaseId = selectedTermbase ? selectedTermbase.id : null

      const translationSettings = map(newTranslationSettings, currentNewTranslationSettings => {
        const {
          translationGuidelineOption,
          srtTranslationText,
        } = currentNewTranslationSettings

        const willUseTranslationGuideline = !!translationGuidelineOption

        const selectedTranslationGuideline = find(translationGuidelines, translationGuideline => translationGuidelineOption === translationGuideline.attributes.name)
        const translationGuidelineId = selectedTranslationGuideline ? selectedTranslationGuideline.id : null

        const willImportTranslationSrt = !isForMultipleProjects && !!srtTranslationText && srtTranslationText.length > 0

        return {
          language: currentNewTranslationSettings.language,
          ...((willUseTranslationGuideline && typeof(translationGuidelineId) === 'number') ? { translationGuidelineId } : {}),
          ...((willImportTranslationSrt ? { srtTranslationText } : {}))
        }
      })

      if (shouldRepurpose && !hasNewTranscription) {
        const payloadCreateClips: ICreateClipsPayload = {
          projectId: projectIdOfFirstEditingProjectVersion,
          projectVersionLanguage,
          outputFormat: outputFormatOption,
          repurposeInstructions,
          themeId: typeof(themeId) === 'number' ? themeId : DEFAULT_THEME_ID,
          ...(willTranslate ? { reviewTranscriptBeforeTranslation: willReviewTranscript } : {}),
          ...(willTranslate ? { translationSettings } : {}),
          ...((willUseTranslationTone && !!formality) ? { formality } : {}),
          ...((willUseTermbase && typeof(termbaseId) === 'number') ? { termbaseId } : {}),
        }

        dispatch(beginProjectSubmission())
        dispatch(createClips(payloadCreateClips))
      } else {
        const selectedCaptionGuideline = find(captionGuidelines, captionGuideline => captionGuidelineOption === captionGuideline.attributes.name)
        const captionGuidelineId = selectedCaptionGuideline ? selectedCaptionGuideline.id : null

        const selectedVocabulary = find(vocabularies, vocabulary => vocabularyOption === vocabulary.attributes.name)
        const vocabularyId = selectedVocabulary? selectedVocabulary.id : null

        const lipSyncModel = willSyncLips && lipSyncOption ? lipSyncOption.replace(' model', '') : null
        const selectedVoiceCloningEngineId = dubbingModeOption === 'Voice-cloning'
          ? getVoiceCloningEngineId(odubModelOption)
          : undefined
        const selectedOdubModelId = getResolvedVoiceCloningModelIdForLanguages(selectedVoiceCloningEngineId, dubbingTargetLanguages)
        const dubbingSettings = {
          createCustomVoices: currentAccountSubscriptionAllowsVoiceCloning,
          ...(selectedVoiceCloningEngineId ? { voiceEngine: selectedVoiceCloningEngineId } : {}),
          shouldSeparateAudio: willIsolateVoices,
          adaptTranslation: willRephraseForDubbing,
          reviewTranslationBeforeDubbing: willReviewTranslations,
          ...(isOdubVoiceCloningModelId(selectedOdubModelId) ? { odubModelId: selectedOdubModelId } : {}),
          ...((willSyncLips && lipSyncModel) ? { lipSyncModel } : {}),
        }

        const payloadSubmitProjectVersionUpdate: ISubmitProjectVersionUpdatePayload = {
          projectVersionIds,
          workspaceId: currentWorkspaceId,
          projectVersionLanguage,
          vocabularyIds: (willUseVocabulary && typeof(vocabularyId) === 'number') ? [vocabularyId] : [],
          includesTranscript: hasNewTranscription,
          reviewTranscriptBeforeTranslation: willReviewTranscript,
          enableSpeakerDiarization: willDetectSpeakers,
          dubbing: willDub,
          repurposing: shouldRepurpose,
          generateShortenedProjectVersion: willGenerateShortenedProjectVersion,
          themeId: typeof(themeId) === 'number' ? themeId : DEFAULT_THEME_ID,
          ...(willTranslate ? { translationSettings } : {}),
          ...((willUseTranslationTone && !!formality) ? { formality } : {}),
          ...(willDub ? dubbingSettings : {}),
          ...(willDub && enhanceAudioOptions.includes(ENHANCE_CLEAN_ORIGINAL_AUDIO) ? { enhanceOptions: {
            enabled: true,
            preProcessing: true,
          } } : {}),
          ...((willUseCaptionGuideline && typeof(captionGuidelineId) === 'number') ? { captionGuidelineId } : {}),
          ...((willUseTermbase && typeof(termbaseId) === 'number') ? { termbaseId } : {}),
          ...((willImportTranscript && hasSrt) ? { srtTranscriptText: transcriptText } : {}),
          ...(willImportTranscript && !hasSrt ? { transcriptText } : {}),
          ...(shouldRepurpose && !!repurposeInstructions ? { repurposeInstructions } : {}),
          ...(shouldRepurpose ? { reviewOriginalBeforeGeneratingClips: willReviewOriginal } : {}),
          ...(shouldRepurpose ? { outputFormat: outputFormatOption } : {})
        }

        dispatch(submitProjectVersionUpdate(payloadSubmitProjectVersionUpdate))
      }
    }
  }

  const getWarningsToShow = () => {
    const warnings: IWarning[] = []

    if (shouldDub && newTranslationLanguagesThatDoNotAllowDubbing.length > 0) {
      warnings.push({ type: 'unsupported_dubbing', info: newTranslationLanguagesThatDoNotAllowDubbing })
    }

    if (shouldDub && !projectVersionLanguageAllowsVoiceCloning) {
      warnings.push({ type: 'unsupported_voice_cloning_source_language', info: [projectVersionLanguage] })
    }

    if (shouldDub && newTranslationLanguagesThatDoNotAllowVoiceCloning.length > 0) {
      warnings.push({ type: 'unsupported_voice_cloning_translation_language', info: newTranslationLanguagesThatDoNotAllowVoiceCloning })
    }

    if (transcriptText.length > 0 && !hasSrt && !newProjectVersionLanguageAllowsTextTranscriptImport) {
      warnings.push({ type: 'unsupported_script_upload' })
    }

    const projectVersionDurationOfZero = elementsForProjectVersions.length > 0 ? some(elementsForProjectVersions, element => !element.duration) : projectVersionDurationForExistingProjects === 0

    if (projectVersionDurationOfZero) {
      warnings.push({ type: 'duration_zero', info: projectVersionIds })
    }

    if (!currentAccountHasCurrentSubscription) {
      warnings.push({ type: 'subscription_not_current' })
    }

    const hasDubbingConflict = !currentAccountSubscriptionAllowsDubbing && shouldDub
    const hasVoiceCloningConflict = !currentAccountSubscriptionAllowsVoiceCloning && shouldDub
    const hasRephrasingForDubbingConflict = !currentAccountSubscriptionAllowsTranslationAdaptionForDubbing && shouldRephraseForDubbing
    const hasLipSyncingConflict = !currentAccountSubscriptionAllowsLipSyncing && shouldSyncLips

    const info = []

    if (hasDubbingConflict) {
      info.push('dubbing')
    }

    if (hasVoiceCloningConflict) {
      info.push('voice-cloning')
    }

    if (hasRephrasingForDubbingConflict) {
      info.push('rephrase for dubbing')
    }

    if (hasLipSyncingConflict) {
      info.push('lip-sync')
    }

    if (hasDubbingConflict || hasVoiceCloningConflict || hasRephrasingForDubbingConflict || hasLipSyncingConflict) {
      warnings.push({ type: 'features_unsupported_by_subscription', info })
    }

    if (isLanguageLimitReached) {
      warnings.push({ type: 'too_many_languages_for_subscription' })
    }

    if (!hasEnoughTotalCredits && !hasEnoughAnalysisCredits) {
      if (warnings.length === 0) {
        setShowTotalCreditsAlert(true)
        setShowAnalysisCreditsAlert(true)
      }
      warnings.push({ type: 'not_enough_total_and_analysis_credits' })
    } else if (!hasEnoughTotalCredits && !willGenerateShortenedProjectVersion) {
      if (warnings.length === 0) {
        setShowTotalCreditsAlert(true)
      }
      warnings.push({ type: 'not_enough_total_credits', info: [formattedCreditsWithHours(totalCosts - currentAccountAvailableTotalCredits)] })
    } else if (!hasEnoughAnalysisCredits) {
      if (warnings.length === 0) {
        setShowAnalysisCreditsAlert(true)
      }
      warnings.push({ type: 'not_enough_analysis_credits', info: [formattedCreditsWithHours(totalAnalysisCost - currentAccountAvailableAnalysisCredits)] })
    }

    return filter(warnings, warning => !ignoredWarnings.includes(warning.type))
  }

  const renderUpload = () => {
    return (
      <UploadAndImport
        currentWorkspaceId={currentWorkspaceId}
        showMissingFileAlert={showMissingFileAlert}
        showFilesTooLargeAlert={showFilesTooLargeAlert}
        elementsForProjectVersions={elementsForProjectVersions}
        setHasStartedFirstUploadOrSubmit={setHasStartedFirstUploadOrSubmit}
        setShowMissingFileAlert={setShowMissingFileAlert}
        setShowFilesTooLargeAlert={setShowFilesTooLargeAlert}
        submittingUrls={submittingUrls}
        maxFileSize={maxFileSize}
      />
    )
  }

  const renderProjectLanguage = () => {
    return (
      <div className="SetupProjectPopup__section">
        <ProjectVersionLanguage
          newProjectVersionLanguage={newProjectVersionLanguage}
          setNewProjectVersionLanguage={setNewProjectVersionLanguage}
          availableProjectVersionLanguages={availableProjectVersionLanguages}
          captionGuidelines={captionGuidelines}
          captionGuidelineOption={captionGuidelineOption}
          setCaptionGuidelineOption={setCaptionGuidelineOption}
          showAlert={showProjectVersionLanguageAlert}
          hasSrt={hasSrt}
          transcriptText={transcriptText}
          setHasSrt={setHasSrt}
          setTranscriptText={setTranscriptText}
          cannotImportScript={cannotImportScript}
          transcriptFileName={transcriptFileName}
          setTranscriptFileName={setTranscriptFileName}
        />
      </div>
    )
  }

  const renderTranslationLanguages = () => {
    return (
      <div className="SetupProjectPopup__section">
        <TranslationLanguages
          projectVersionLanguageIsTranslatable={projectVersionLanguageIsTranslatable}
          isNewProject={isNewProject}
          isNewSubtitlesProject={isNewSubtitlesProject}
          newProjectVersionLanguage={newProjectVersionLanguage}
          existingProjectVersionLanguage={existingProjectVersionLanguage}
          newTranslationSettings={newTranslationSettings}
          existingTranslationLanguages={existingTranslationLanguages}
          availableTranslationLanguages={availableTranslationLanguages}
          setNewTranslationSettings={setNewTranslationSettings}
          showAlert={showTranslationLanguagesAlert}
          defaultTranslationGuidelineOption={defaultTranslationGuidelineOption}
          translationGuidelines={translationGuidelines}
          cannotImportScript={cannotImportScript}
          translationProvider={translationProvider}
          fetchingTranslationLanguages={fetchingTranslationLanguages}
          handleFetchTranslationLanguages={handleFetchTranslationLanguages}
          shouldTranslate={shouldTranslate}
          setShouldTranslate={setShouldTranslate}
          shouldRepurpose={shouldRepurpose}
        />
      </div>
    )
  }

  const renderInstructions = () => {
    return (
      <div className="SetupProjectPopup__section">
        <RepurposeInstructions
          repurposeInstructions={repurposeInstructions}
          setRepurposeInstructions={setRepurposeInstructions}
        />
      </div>
    )
  }

  const renderParameters = (isForAdvancedParameters: boolean) => {
    return (
      <div className="SetupProjectPopup__section">
        <Parameters
          isForAdvancedParameters={isForAdvancedParameters}
          isNewSubtitlesProjectWithoutTranslation={isNewSubtitlesProjectWithoutTranslation}
          isNewDubbingProject={isNewDubbingProject}
          isAddingLanguagesToExistingProject={isAddingLanguagesToExistingProject}
          isAddingClipsToExistingProject={isAddingClipsToExistingProject}
          isDubbingProject={isDubbingProject}
          isSubtitlesProject={isSubtitlesProject}
          isAddingClipsToExistingProjectWithoutTranslation={isAddingClipsToExistingProjectWithoutTranslation}
          reviewModeOption={reviewModeOption}
          setReviewModeOption={setReviewModeOption}
          reviewModeOptionsForRepurpose={reviewModeOptionsForRepurpose}
          setReviewModeOptionsForRepurpose={setReviewModeOptionsForRepurpose}
          reviewModeOptionsForDubbing={reviewModeOptionsForDubbing}
          setReviewModeOptionsForDubbing={setReviewModeOptionsForDubbing}
          styleOption={styleOption}
          setStyleOption={setStyleOption}
          vocabularyOption={vocabularyOption}
          setVocabularyOption={setVocabularyOption}
          termbaseOption={termbaseOption}
          setTermbaseOption={setTermbaseOption}
          speakerDetectionOption={speakerDetectionOption}
          setSpeakerDetectionOption={setSpeakerDetectionOption}
          translationToneOption={translationToneOption}
          setTranslationToneOption={setTranslationToneOption}
          dubbingModeOption={dubbingModeOption}
          setDubbingModeOption={setDubbingModeOption}
          odubModelOption={odubModelOption}
          setOdubModelOption={setOdubModelOption}
          dubbingTargetLanguages={dubbingTargetLanguages}
          rephrasingForDubbingOption={rephrasingForDubbingOption}
          setRephrasingForDubbingOption={setRephrasingForDubbingOption}
          isolateVoicesOption={isolateVoicesOption}
          setIsolateVoicesOption={setIsolateVoicesOption}
          lipSyncOption={lipSyncOption}
          setLipSyncOption={setLipSyncOption}
          enhanceAudioOptions={enhanceAudioOptions}
          setEnhanceAudioOptions={setEnhanceAudioOptions}
          outputFormatOption={outputFormatOption}
          setOutputFormatOption={setOutputFormatOption}
          themes={themes}
          vocabularies={vocabularies}
          termbases={termbases}
          setShowStylingPopup={setShowStylingPopup}
          currentAccountSubscriptionAllowsVoiceCloning={currentAccountSubscriptionAllowsVoiceCloning}
          shouldRepurpose={shouldRepurpose}
          shouldDub={shouldDub}
          aiDubbingEnabled={shouldDub}
          willTranslate={willTranslate}
          hasNewTranscription={hasNewTranscription}
        />
      </div>
    )
  }

  const renderMainForm = () => {
    return (
      <div className="SetupProjectPopup__main">
        {isNewProject && renderProjectLanguage()}
        {renderTranslationLanguages()}
        {shouldRepurpose && renderInstructions()}
        {renderParameters(false)}
        {renderParameters(true)}
      </div>
    )
  }

  const renderBottom = () => {
    return (
      <div className="SetupProjectPopup__bottom">
        <div className="SetupProjectPopup__bottom-left">
          <Button
            caption="Cancel"
            icon={<Cancel />}
            onClick={handleClose}
            styleType="gray-subtle"
            size="small"
            disabled={projectSubmissionIsPending}
            data-testid="project-setup-cancel"
          />
          {!shouldHideTotalCosts && (
            <div className="SetupProjectPopup__costs">
              {`Usage of credits: ${formattedCreditsWithHours(totalCosts)}`}
            </div>
          )}
          {(!shouldHideAnalysisCosts || !shouldHideEstimatedClipTranslationCosts) && (
            <div className="SetupProjectPopup__costs-clip">
              {!shouldHideAnalysisCosts && (
                <div
                  className={classnames(
                    'SetupProjectPopup__costs',
                    { 'SetupProjectPopup__costs-alert': showAnalysisCreditsAlert }
                  )}
                >
                  {`${showAnalysisCreditsAlert ? '🔴 ' : '⚡ '} Analysis: ${formattedCreditsWithHours(totalAnalysisCost)}`}
                </div>
              )}
              {!shouldHideEstimatedClipTranslationCosts && (
                <div
                  className={classnames(
                    'SetupProjectPopup__costs',
                    { 'SetupProjectPopup__costs-alert': showTotalCreditsAlert }
                  )}
                >
                  {`${showTotalCreditsAlert ? '🔴 ' : '🌍 '} Translation: ~${formattedCreditsWithHours(estimatedClipTranslationCosts)} (est.)`}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="SetupProjectPopup__bottom-right">
          <StepIndicator
            currentStep={2}
            maxSteps={2}
            stepName="Settings"
          />
          {shouldShowUploadingButtonInsteadOfContinue ? (
            <div className="SetupProjectPopup__uploading-button-wrapper">
              <Button
                caption="Uploading"
                icon={<Loader />}
                styleType="brand"
                size="small"
                disabled
              />
            </div>
          ) : (
            <Button
              caption={shouldRepurpose ? 'Generate Clips' : 'Continue'}
              icon={<Continue />}
              onClick={handleContinue}
              styleType="brand"
              size="small"
              iconRight
              disabled={shouldDisableCTAButton}
              data-testid="project-create-submit"
            />
          )}
        </div>
      </div>
    )
  }

  const renderProjectSetupPopup = () => {
    return (
      <Popup
        className="SetupProjectPopup"
        zIndex={200000}
        shouldNotRenderOverlay={newProjectPopupIsOpen}
        closePopup={projectSubmissionIsPending ? undefined : handleClose}
      >
        <div className="SetupProjectPopup__header">
          {header}
        </div>
        {showUpload && renderUpload()}
        {showMain && renderMainForm()}
        {renderBottom()}
      </Popup>
    )
  }

  const renderWarningPopup = () => {
    return (
      <SetupWarningPopup
        warningsToShow={warningsToShow}
        handleClose={handleClose}
        handleCloseAndKeepEverything={handleCloseAndKeepEverything}
        submit={submit}
        resetWarnings={resetWarnings}
        updateIgnoredWarningsAndTrySubmit={updateIgnoredWarningsAndTrySubmit}
        hasNewTranscription={hasNewTranscription}
        projectVersionLanguage={projectVersionLanguage}
        userIsAccountAdmin={userIsAccountAdmin}
        adminEmails={currentAccountAdminEmails}
        currentAccountSubscriptionMaxLanguagesLimit={currentAccountSubscriptionMaxLanguagesLimit}
        totalNumberOfSubscriptionSubtitles={totalNumberOfSubscriptionSubtitles}
        isNonExistingProject={isNonExistingProject}
        newTranslationLanguages={newTranslationLanguages}
        shouldNotRenderOverlay={newProjectPopupIsOpen}
      />
    )
  }

  const renderStylingPopup = () => {
    return (
      <SetupStylingPopup
        setShowStylingPopup={setShowStylingPopup}
        themes={themes}
        styleOption={styleOption}
        setStyleOption={setStyleOption}
        shouldNotRenderOverlay={newProjectPopupIsOpen}
      />
    )
  }

  const handleVoiceTypeUpgradePopupClose = () => {
    dispatch(closeVoiceTypeUpgradePopup())
  }

  const handleVoiceTypeUpgradePopupConfirm = () => {
    handleClose()
    setTimeout(() => {
      // We must wait for handleClose to finish before navigating
      router.navigate('/subscriptions')
    }, DELAY_WAITING_FOR_HANDLE_CLOSE)
  }

  const renderVoiceTypeUpgradePopup = () => {
    return (
      <Popup
        className="VoiceTypeUpgradePopup"
        closePopup={handleVoiceTypeUpgradePopupClose}
        zIndex={VOICE_TYPE_UPGRADE_POPUP_Z_INDEX}
      >
        <VoiceTypeUpgradePopup
          onUpgrade={handleVoiceTypeUpgradePopupConfirm}
          onReturn={handleVoiceTypeUpgradePopupClose}
          canManageSubscription={userIsAccountAdmin && !currentAccountHasEnterpriseOrCustomSubscription}
          tooltipDelay={window.Checksub.tooltipDelay}
          voiceType={voiceTypeUpgradePopupLabel!}
        />
      </Popup>
    )
  }

  return (
    <React.Fragment>
      {hasLoadedSuccessfully && !showWarning && !showStylingPopup && renderProjectSetupPopup()}
      {showWarning && renderWarningPopup()}
      {showStylingPopup && renderStylingPopup()}
      {voiceTypeUpgradePopupLabel && renderVoiceTypeUpgradePopup()}
    </React.Fragment>
  )
}

export default SetupProjectPopup
